import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import Autorenew from '@mui/icons-material/Autorenew';
import { Box, Button, Grid } from '@mui/material';
import { FC, useRef } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';

import {
  PieceAssetSize,
  PieceAssetType,
  RecipientAutoDetectionStatus,
  TrackingNumberStatus,
  Workflow,
} from '@apiContract';
import DropdownMenuActions from '@components/Capture/components/DropdownMenuActions';
import { BarcodeLocalIcon } from '@components/Capture/icons/BarcodeLocalIcon';
import { LocationIcon } from '@components/Capture/icons/LocationIcon';
import { PersonPinRoundedIcon } from '@components/Capture/icons/PersonPinRoundedIcon';
// eslint-disable-next-line import/no-named-as-default
import { Lightbox, useLightBox } from '@components/Lightbox';
import RecipientNotification from '@components/RecipientNotification';
import { useSettings } from '@hooks/useSettings';
import { ImagePreview } from '@uiKit/ImagePreview';

import { getPieceAsset, isPieceSend } from '@utils/piece';

import { PrintIcon } from '../../../../icons/PrintIcon';
import { CapturePiece, CurrentSessionProps, DrawerTypes, IdV4, Steps } from '../../../../types';
import {
  sxBackBtn,
  sxConstainer,
  sxContent,
  sxHeaderContainer,
  sxHeaderContainerLine,
  sxImageContainer,
  sxPX2,
} from './styles';

type PieceViewProps = {
  handleBack: () => void;
  capturePiece: CapturePiece;
  setDrawer: (type: DrawerTypes) => void;
  onCropClick: (returnPath: Steps) => void;
  retryFailedPieces: (id: IdV4) => void;
  currentSession: CurrentSessionProps;
  onDeletePiece: () => void;
};

const Piece: FC<PieceViewProps> = ({
  handleBack,
  capturePiece,
  setDrawer,
  onDeletePiece,
  onCropClick,
  retryFailedPieces,
  currentSession,
}) => {
  const { piece } = capturePiece;
  const isArchived = piece?.pieceSummary?.trackingNumberStatus === TrackingNumberStatus.None;

  const photoref = useRef('');

  const settings: Record<string, boolean> = useSettings(['LabelPrintingEnabled']);

  const { workflow } = currentSession?.params || {};

  const setDrawerIfPieceExists = (drawerType: DrawerTypes) => {
    if (piece) {
      setDrawer(drawerType);
    }
  };

  const pieceAsset = getPieceAsset(piece?.pieceAssets || [], PieceAssetSize.Large);

  if (!photoref.current && capturePiece.photo && capturePiece.photo instanceof File) {
    photoref.current = URL.createObjectURL(capturePiece.photo);
  }

  const pieceSrc =
    capturePiece.editImage ||
    pieceAsset?.url ||
    (capturePiece.photo instanceof File ? photoref.current : capturePiece.photo);

  const lightbox = useLightBox([{ src: pieceSrc || '' }]);

  const isSend = isPieceSend(capturePiece?.piece);

  const getActiveButton = () => {
    if (capturePiece.status === 'failed') {
      return (
        <Button
          onClick={() => {
            retryFailedPieces(capturePiece.id as IdV4);
          }}
          variant="soft"
          sx={sxPX2}
          startIcon={<Autorenew />}
        >
          Retry
        </Button>
      );
    }

    return (
      <>
        <PersonPinRoundedIcon capturePiece={capturePiece} onClick={() => setDrawerIfPieceExists(DrawerTypes.User)} />

        <BarcodeLocalIcon
          capturePiece={capturePiece}
          workflow={workflow}
          disabled={isArchived}
          onClick={() => setDrawerIfPieceExists(DrawerTypes.Barcode)}
        />

        {workflow && ![Workflow.HighDensityMail, Workflow.MailBox, Workflow.Lockers].includes(workflow) ? (
          <LocationIcon capturePiece={capturePiece} onClick={() => setDrawerIfPieceExists(DrawerTypes.Location)} />
        ) : null}

        {settings.LabelPrintingEnabled && <PrintIcon piece={capturePiece.piece} />}

        <DropdownMenuActions
          capturePiece={capturePiece}
          setDrawer={setDrawer}
          onDeletePiece={onDeletePiece}
          onCropClick={onCropClick}
          currentSession={currentSession}
        />
      </>
    );
  };

  const showNotification =
    piece?.recipient ||
    piece?.recipientAutoDetection?.status === RecipientAutoDetectionStatus.IdentifiedVerificationRequired;

  return (
    <Grid container sx={sxConstainer}>
      <Grid container justifyContent="flex-end" direction={{ xs: 'column-reverse', md: 'row' }} sx={sxHeaderContainer}>
        <Grid sx={sxHeaderContainerLine}>
          <Box display="flex" justifyContent="space-between" height={1} alignItems="center">
            <Button onClick={handleBack} variant="text" startIcon={<ArrowBackIosNewIcon />} sx={sxBackBtn}>
              Back
            </Button>
            <Box display="flex" gap={1}>
              {!isSend && getActiveButton()}
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Grid flex={1} sx={sxContent}>
        <Box display="flex" sx={sxImageContainer}>
          <AutoSizer>
            {({ height, width }) => (
              <ImagePreview
                key={pieceSrc}
                imageDataUrl={pieceSrc}
                croppingBox={pieceAsset?.croppingBox}
                height={height}
                width={width}
                isDrawAnnotations={!!pieceAsset?.croppingBox || pieceAsset?.pieceAssetType !== PieceAssetType.Cropped}
                onAnnotationClick={isArchived ? undefined : () => setDrawer(DrawerTypes.Barcode)}
                annotations={{
                  workflow: currentSession.receiveSession?.workflow,
                  items: capturePiece.pieceAnnotations,
                }}
              />
            )}
          </AutoSizer>

          {showNotification && <RecipientNotification piece={capturePiece.piece} />}
        </Box>
        <Lightbox
          disableSlideshow
          disableTotal
          disableThumbnails
          disableCaptions
          render={{
            buttonPrev: () => null,
            buttonNext: () => null,
          }}
          slides={[{ src: pieceSrc || '' }]}
          open={lightbox.open}
          close={lightbox.onClose}
          zoom={{
            maxZoomPixelRatio: 2.5,
            zoomInMultiplier: 1.5,
            doubleTapDelay: 300,
            doubleClickDelay: 300,
            doubleClickMaxStops: 2,
            keyboardMoveDistance: 50,
            wheelZoomDistanceFactor: 100,
            pinchZoomDistanceFactor: 100,
            scrollToZoom: true,
          }}
        />
      </Grid>
    </Grid>
  );
};

export default Piece;
